import * as Yup from "yup";

const numberRegex = /^[0-9]*$/;
const yearRegex = /^[0-9]{4}$/;
const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|1\d|2\d|3[01])\/\d{4}$/;
const noEmojiRegex = /\p{Extended_Pictographic}/u;
const emailRegex =
  /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$/;
const noSpecialCharRegex = /^[a-zA-Z ]+$/;

const wageCheck = (value: string | undefined) => {
  if (!value) return false;
  const wageRegex = /^([0-9]+([.][0-9]*)?|[.][0-9]+)$/;
  const splitWage = value.split(".");
  if (splitWage[1] && splitWage[1].length > 2) {
    return false;
  }
  return wageRegex.test(value);
};

const addressRegex =
  /^(?!.*(?:(.*((p|post)[-.\s]*(o|off|office)[-.\s]*(box|bin)[-.\s]*)|.*((p |post)[-.\s]*(box|bin)[-.\s]*)))).*$/i;

const postalcodeRegex = /(^\d{5}$)|(^\d{5}-\d{4}$)/;
const einRegex = /^[0-9]{2}-?[0-9]{7}$/;

export const businessDetailsValidationSchema = Yup.object().shape({
  companyName: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  dba: Yup.string()
    .optional()
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  industry: Yup.string().required("You missed this one"),
  industryNaics6: Yup.string().when("industry", {
    is: "Other",
    then: Yup.string().notRequired(),
    otherwise: Yup.string()
      .required("You missed this one")
      .test(
        "no emoji",
        "Sorry, no emoji 😔",
        (value) => !noEmojiRegex.test(value || "")
      ),
  }),
  industryNaics4: Yup.string().when("industry", {
    is: "Other",
    then: Yup.string().notRequired(),
    otherwise: Yup.string()
      .required("You missed this one")
      .test(
        "no emoji",
        "Sorry, no emoji 😔",
        (value) => !noEmojiRegex.test(value || "")
      ),
  }),
  industryOther: Yup.string().when("industry", {
    is: "Other",
    then: Yup.string()
      .required("You missed this one")
      .test(
        "no emoji",
        "Sorry, no emoji 😔",
        (value) => !noEmojiRegex.test(value || "")
      ),
  }),
  ein: Yup.string()
    .required("You missed this one")
    .matches(einRegex, "EIN is only 9 characters with numbers only, please."),
  entityType: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  isNonProfit: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  yearFounded: Yup.string()
    .matches(yearRegex, "Please enter a year in the YYYY format")
    .required("You missed this one")
    .test(
      "year validation",
      `Please enter a year between 1900-${new Date().getFullYear()}`,
      (value) =>
        Number(value) >= 1900 && Number(value) <= new Date().getFullYear()
    ),
  hoursOfOperation: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  ownerHalfStakeOther: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  relatedEntities: Yup.array().when("ownerHalfStakeOther", {
    is: "Yes",
    then: Yup.array().of(
      Yup.object().shape({
        reName: Yup.string()
          .required("You missed this one")
          .test(
            "no emoji",
            "Sorry, no emoji 😔",
            (value) => !noEmojiRegex.test(value || "")
          ),
        reDba: Yup.string()
          .optional()
          .test(
            "no emoji",
            "Sorry, no emoji 😔",
            (value) => !noEmojiRegex.test(value || "")
          ),
        reDescription: Yup.string()
          .required("You missed this one")
          .max(255, "Less than 255 characters, please.")
          .test(
            "no emoji",
            "Sorry, no emoji 😔",
            (value) => !noEmojiRegex.test(value || "")
          ),
        reHasW2Employees: Yup.string()
          .required("You missed this one")
          .test(
            "no emoji",
            "Sorry, no emoji 😔",
            (value) => !noEmojiRegex.test(value || "")
          ),
        reEin: Yup.string().when("reHasW2Employees", {
          is: "Yes",
          then: Yup.string()
            .required("You missed this one")
            .matches(
              einRegex,
              "EIN is only 9 characters with numbers only, please."
            ),
        }),
        reIsDependent: Yup.string().when("reHasW2Employees", {
          is: "Yes",
          then: Yup.string().required("You missed this one"),
        }),
        reHasOverlappingEmployees: Yup.string().when("reHasW2Employees", {
          is: "Yes",
          then: Yup.string().required("You missed this one"),
        }),
        reSharesLocation: Yup.string().when("reHasW2Employees", {
          is: "Yes",
          then: Yup.string().required("You missed this one"),
        }),
      })
    ),
  }),
});

export const describeBusinessValidationSchema = Yup.object().shape({
  businessDescription: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  website: Yup.array()
    .of(
      Yup.string()
        .url(
          "Be sure to add a complete URL including the http:// and the suffix, like .com so we can find your company website."
        )
        .test("no emoji", "Sorry, no emoji 😔", (value) => {
          return !noEmojiRegex.test(value || "");
        })
        .test(
          "atLeastOneValidWebsite",
          "At least one website is required",
          (_, schema) => {
            return !(
              !schema.parent?.some((website: string) => !!website) &&
              schema.path === `website[${schema.parent.length - 1}]`
            );
          }
        )
        .test(
          "duplicatedValues",
          "This is a duplicate URL",
          (value, schema) => {
            const valueIndex = +(schema.path.at(-2) || 0);
            const hasDuplicateUrl = schema.parent.some(
              (url: string, index: number) => {
                return value && url === value && valueIndex !== index;
              }
            );

            return !hasDuplicateUrl;
          }
        )
    )
    .min(1, "At least one website is required")
    .required("At least one website is required"),
  verifyCompanyUrl: Yup.string().optional(),
  dailyOperationsOfEmployees: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  hasUnionWorkers: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
});

export const epliValidationSchema = Yup.object().shape({
  hasPolicy: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  epliDocument: Yup.string().when("hasPolicy", {
    is: "Yes",
    then: Yup.string().required("You missed this one"),
  }),
  epliLossDocument: Yup.string().optional(),
  noEpliLossRunsReportReason: Yup.string()
    .optional()
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  noEpliLossRunsReportOtherReason: Yup.string().when(
    "noEpliLossRunsReportReason",
    {
      is: "Other",
      then: Yup.string()
        .required("You missed this one")
        .test(
          "no emoji",
          "Sorry, no emoji 😔",
          (value) => !noEmojiRegex.test(value || "")
        ),
    }
  ),
  receivedEpliAllegations: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  beenSuedFormerEmp: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  claimDetails: Yup.string().when("beenSuedFormerEmp", {
    is: "Yes",
    then: Yup.string()
      .required("You missed this one")
      .test(
        "no emoji",
        "Sorry, no emoji 😔",
        (value) => !noEmojiRegex.test(value || "")
      ),
  }),
  overTwentyFiveEe: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  offeredSeverancePackages: Yup.string().when("overTwentyFiveEe", {
    is: "Yes",
    then: Yup.string()
      .required("You missed this one")
      .test(
        "no emoji",
        "Sorry, no emoji 😔",
        (value) => !noEmojiRegex.test(value || "")
      ),
  }),
});

export const businessAttributesValidationSchema = Yup.object().shape({
  hasPriorCarriers: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  priorCarriers: Yup.string().when("hasPriorCarriers", {
    is: "Yes",
    then: Yup.string()
      .required("You missed this one")
      .test(
        "no emoji",
        "Sorry, no emoji 😔",
        (value) => !noEmojiRegex.test(value || "")
      ),
  }),
  hasHadWorkersCompPolicy: Yup.string().when("hasPriorCarriers", {
    is: "Yes",
    then: Yup.string().required("You missed this one"),
  }),
  beenCancelled: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  policyCancelledReason: Yup.string().when("beenCancelled", {
    is: "Yes",
    then: Yup.string()
      .required("You missed this one")
      .test(
        "no emoji",
        "Sorry, no emoji 😔",
        (value) => !noEmojiRegex.test(value || "")
      ),
  }),
  hasPastDuePremiums: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  beenDeclined: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  sponsorSportsTeams: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  hasTaxLeinBankrupcy: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  offerHealthInsurance: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  ownAircraftWatercraft: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  use3rdPartyVendors: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  thirdPartyDetails1: Yup.string().when("use3rdPartyVendors", {
    is: "Yes",
    then: Yup.string()
      .required("You missed this one")
      .test(
        "no emoji",
        "Sorry, no emoji 😔",
        (value) => !noEmojiRegex.test(value || "")
      ),
  }),
  thirdPartyDetails2: Yup.string().when("use3rdPartyVendors", {
    is: "Yes",
    then: Yup.string()
      .required("You missed this one")
      .test(
        "no emoji",
        "Sorry, no emoji 😔",
        (value) => !noEmojiRegex.test(value || "")
      ),
  }),
  thirdPartyDetails3: Yup.string().when("use3rdPartyVendors", {
    is: "Yes",
    then: Yup.string()
      .required("You missed this one")
      .test(
        "no emoji",
        "Sorry, no emoji 😔",
        (value) => !noEmojiRegex.test(value || "")
      ),
  }),
  thirdPartyDetails4: Yup.string().when("use3rdPartyVendors", {
    is: "Yes",
    then: Yup.string()
      .required("You missed this one")
      .test(
        "no emoji",
        "Sorry, no emoji 😔",
        (value) => !noEmojiRegex.test(value || "")
      ),
  }),
  hasVolunteers: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  numberVolunteers: Yup.string().when("hasVolunteers", {
    is: "Yes",
    then: Yup.string()
      .required("You missed this one")
      .matches(numberRegex, "Numbers only, please.")
      .test(
        "no emoji",
        "Sorry, no emoji 😔",
        (value) => !noEmojiRegex.test(value || "")
      ),
  }),
  volunteerActivities: Yup.string().when("hasVolunteers", {
    is: "Yes",
    then: Yup.string()
      .required("You missed this one")
      .test(
        "no emoji",
        "Sorry, no emoji 😔",
        (value) => !noEmojiRegex.test(value || "")
      ),
  }),
  hasVolunteerAccidentPolicy: Yup.string().when("hasVolunteers", {
    is: "Yes",
    then: Yup.string()
      .required("You missed this one")
      .test(
        "no emoji",
        "Sorry, no emoji 😔",
        (value) => !noEmojiRegex.test(value || "")
      ),
  }),
  volunteerAccidentPolicy: Yup.string().when("hasVolunteerAccidentPolicy", {
    is: "Yes",
    then: Yup.string().required("You missed this one"),
  }),
  volunteerTravel: Yup.string().when("hasVolunteers", {
    is: "Yes",
    then: Yup.string()
      .required("You missed this one")
      .test(
        "no emoji",
        "Sorry, no emoji 😔",
        (value) => !noEmojiRegex.test(value || "")
      ),
  }),
  hazardousMats: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  materialSafetyDataSheets: Yup.string().optional(),
  hasWrittenSafteyProg: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
});

export const employeeAttributesValidationSchema = Yup.object().shape({
  travel: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  employeesWhoTravel: Yup.array().when("travel", {
    is: "Yes",
    then: Yup.array().of(
      Yup.object().shape({
        employeeId: Yup.string().required("You missed this one"),
        travelFrequency: Yup.string()
          .required("You missed this one")
          .test(
            "no emoji",
            "Sorry, no emoji 😔",
            (value) => !noEmojiRegex.test(value || "")
          ),
        travelInternationally: Yup.string()
          .required("You missed this one")
          .test(
            "no emoji",
            "Sorry, no emoji 😔",
            (value) => !noEmojiRegex.test(value || "")
          ),
        travelLocation: Yup.string().when("travelInternationally", {
          is: "Yes",
          then: Yup.string()
            .required("You missed this one")
            .test(
              "no emoji",
              "Sorry, no emoji 😔",
              (value) => !noEmojiRegex.test(value || "")
            ),
          otherwise: Yup.string().notRequired(),
        }),
      })
    ),
  }),
  hardware: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  employeesWhoUseTools: Yup.array().when("hardware", {
    is: "Yes",
    then: Yup.array().of(
      Yup.object().shape({
        employeeId: Yup.string().required("You missed this one"),
        hardwareNames: Yup.string()
          .required("You missed this one")
          .test(
            "no emoji",
            "Sorry, no emoji 😔",
            (value) => !noEmojiRegex.test(value || "")
          ),
      })
    ),
  }),
  employeesPhysicalLabor: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  employeesWhoRequirePhysicalLabor: Yup.array().when("employeesPhysicalLabor", {
    is: "Yes",
    then: Yup.array().of(
      Yup.object().shape({
        employeeId: Yup.string().required("You missed this one"),
        percentPhysicalLabor: Yup.string()
          .required("You missed this one")
          .test(
            "no emoji",
            "Sorry, no emoji 😔",
            (value) => !noEmojiRegex.test(value || "")
          ),
      })
    ),
  }),
  hasContractors: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  employmentDuration: Yup.string().when("hasContractors", {
    is: "Yes",
    then: Yup.string()
      .required("You missed this one")
      .test(
        "no emoji",
        "Sorry, no emoji 😔",
        (value) => !noEmojiRegex.test(value || "")
      ),
  }),
  numberContractors: Yup.string().when("hasContractors", {
    is: "Yes",
    then: Yup.string()
      .required("You missed this one")
      .test(
        "no emoji",
        "Sorry, no emoji 😔",
        (value) => !noEmojiRegex.test(value || "")
      ),
  }),
  percentContractors: Yup.string().when("hasContractors", {
    is: "Yes",
    then: Yup.string()
      .required("You missed this one")
      .test(
        "no emoji",
        "Sorry, no emoji 😔",
        (value) => !noEmojiRegex.test(value || "")
      ),
  }),
  contractorWorkType: Yup.string().when("hasContractors", {
    is: "Yes",
    then: Yup.string()
      .required("You missed this one")
      .test(
        "no emoji",
        "Sorry, no emoji 😔",
        (value) => !noEmojiRegex.test(value || "")
      ),
  }),
  contractorsHaveWorkComp: Yup.string().when("hasContractors", {
    is: "Yes",
    then: Yup.string()
      .required("You missed this one")
      .test(
        "no emoji",
        "Sorry, no emoji 😔",
        (value) => !noEmojiRegex.test(value || "")
      ),
  }),
  contractorsHaveWorkersCompCois: Yup.string().when("contractorsHaveWorkComp", {
    is: "Yes",
    then: Yup.string()
      .required("You missed this one")
      .test(
        "no emoji",
        "Sorry, no emoji 😔",
        (value) => !noEmojiRegex.test(value || "")
      ),
  }),
  numberTemp: Yup.string()
    .required("You missed this one")
    .matches(numberRegex, "Numbers only, please.")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  boatsBridges: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  employeesWhoUseBoatsBridges: Yup.array().when("boatsBridges", {
    is: "Yes",
    then: Yup.array()
      .of(Yup.string())
      .min(1, "You missed this one")
      .required("You missed this one"),
  }),
  employeesWorkUnderground: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  employeesWhoWorkUnderground: Yup.array().when("employeesWorkUnderground", {
    is: "Yes",
    then: Yup.array().of(
      Yup.object().shape({
        employeeId: Yup.string().required("You missed this one"),
        undergroundDescription: Yup.string()
          .required("You missed this one")
          .test(
            "no emoji",
            "Sorry, no emoji 😔",
            (value) => !noEmojiRegex.test(value || "")
          ),
      })
    ),
  }),
  employeesWorkOnLadders: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  employeesWhoWorkOnLadders: Yup.array().when("employeesWorkOnLadders", {
    is: "Yes",
    then: Yup.array().of(
      Yup.object().shape({
        employeeId: Yup.string().required("You missed this one"),
        laddersDescription: Yup.string()
          .required("You missed this one")
          .test(
            "no emoji",
            "Sorry, no emoji 😔",
            (value) => !noEmojiRegex.test(value || "")
          ),
      })
    ),
  }),
  employeePerformHeavyLifting: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  employeesWhoPerformHeavyLifting: Yup.array().when(
    "employeePerformHeavyLifting",
    {
      is: "Yes",
      then: Yup.array().of(
        Yup.object().shape({
          employeeId: Yup.string().required("You missed this one"),
          maximumWeightLifted: Yup.string()
            .required("You missed this one")
            .test(
              "no emoji",
              "Sorry, no emoji 😔",
              (value) => !noEmojiRegex.test(value || "")
            ),
        })
      ),
    }
  ),
  deliveries: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  employeesWhoMakeDeliveries: Yup.array().when("deliveries", {
    is: "Yes",
    then: Yup.array().of(
      Yup.object().shape({
        employeeId: Yup.string().required("You missed this one"),
        deliveryFrequency: Yup.string()
          .required("You missed this one")
          .test(
            "no emoji",
            "Sorry, no emoji 😔",
            (value) => !noEmojiRegex.test(value || "")
          ),
        deliveryDistance: Yup.string()
          .required("You missed this one")
          .test(
            "no emoji",
            "Sorry, no emoji 😔",
            (value) => !noEmojiRegex.test(value || "")
          ),
        deliveryTransportationType: Yup.string()
          .required("You missed this one")
          .test(
            "no emoji",
            "Sorry, no emoji 😔",
            (value) => !noEmojiRegex.test(value || "")
          ),
      })
    ),
  }),
  physicalDisability: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  employeesWhoHavePhysicalDisabilities: Yup.array().when("physicalDisability", {
    is: "Yes",
    then: Yup.array()
      .of(Yup.string())
      .min(1, "You missed this one")
      .required("You missed this one"),
  }),
  underageOrSenior: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  underageOrSeniorEmployees: Yup.array().when("underageOrSenior", {
    is: "Yes",
    then: Yup.array().of(
      Yup.object().shape({
        employeeId: Yup.string().required("You missed this one"),
        jobTitle: Yup.string()
          .max(50, "Less than 50 characters, please.")
          .required("You missed this one")
          .test(
            "no emoji",
            "Sorry, no emoji 😔",
            (value) => !noEmojiRegex.test(value || "")
          ),
        dateOfBirth: Yup.string()
          .matches(
            dateRegex,
            "Please update the birth date format to MM/DD/YYYY"
          )
          .required("You missed this one")
          .test(
            "future date",
            "Hello time traveler! Please pick date in the past",
            (value) => new Date(value || "") <= new Date()
          ),
      })
    ),
  }),
});

export const documentsValidationSchema = Yup.object().shape({
  incorporationDocuments: Yup.string().required("You missed this one"),
  lossRunsReport: Yup.string().optional(),
  noLossRunsReportReason: Yup.string().when("lossRunsReport", {
    is: (lossRunsReport: any) => !lossRunsReport,
    then: Yup.string().required("You missed this one"),
  }),
  noLossRunsReportOtherReason: Yup.string().when("noLossRunsReportReason", {
    is: "Other",
    then: Yup.string().required("You missed this one"),
  }),
});

export const ownerAndEmployeeValidationSchema = Yup.object().shape({
  firstName: Yup.string()
    .max(50, "Less than 50 characters, please.")
    .required("You missed this one")
    .matches(noSpecialCharRegex, "No special characters or numbers, please."),
  middleName: Yup.string()
    .optional()
    .max(50, "Less than 50 characters, please.")
    .matches(noSpecialCharRegex, "No special characters or numbers, please."),
  lastName: Yup.string()
    .max(50, "Less than 50 characters, please.")
    .required("You missed this one")
    .matches(noSpecialCharRegex, "No special characters or numbers, please."),
  isRemote: Yup.string().required("You missed this one"),
  officeLocationId: Yup.string().when("isRemote", {
    is: "No",
    then: Yup.string()
      .required("You missed this one")
      .test(
        "no emoji",
        "Sorry, no emoji 😔",
        (value) => !noEmojiRegex.test(value || "")
      ),
  }),
  address1: Yup.string()
    .matches(
      addressRegex,
      "No PO Boxes, business mailing addresses, or virtual mailboxes."
    )
    .when("isRemote", {
      is: "Yes",
      then: Yup.string()
        .required("You missed this one")

        .max(50, "Less than 50 characters, please.")
        .test(
          "no emoji",
          "Sorry, no emoji 😔",
          (value) => !noEmojiRegex.test(value || "")
        ),
    }),
  address2: Yup.string()
    .optional()
    .max(50, "Less than 50 characters, please.")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  city: Yup.string().when("isRemote", {
    is: "Yes",
    then: Yup.string()
      .required("You missed this one")
      .max(50, "Less than 50 characters, please.")
      .test(
        "no emoji",
        "Sorry, no emoji 😔",
        (value) => !noEmojiRegex.test(value || "")
      ),
  }),
  state: Yup.string().when("isRemote", {
    is: "Yes",
    then: Yup.string()
      .required("You missed this one")
      .test(
        "no emoji",
        "Sorry, no emoji 😔",
        (value) => !noEmojiRegex.test(value || "")
      ),
  }),
  zip: Yup.string()
    .matches(
      postalcodeRegex,
      "Zip code should be 5 digits or 5+4 digits. For example, 10041 or 10041-0004."
    )
    .when("isRemote", {
      is: "Yes",
      then: Yup.string()
        .required("You missed this one")
        .test(
          "no emoji",
          "Sorry, no emoji 😔",
          (value) => !noEmojiRegex.test(value || "")
        ),
    }),
  censusType: Yup.string().required("You missed this one"),
  title: Yup.string()
    .max(50, "Less than 50 characters, please.")
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  roleDescription: Yup.string()
    .required("You missed this one")
    .test(
      "no emoji",
      "Sorry, no emoji 😔",
      (value) => !noEmojiRegex.test(value || "")
    ),
  email: Yup.string()
    .matches(emailRegex, "Please enter a valid email address.")
    .required("You missed this one"),
  ownerStake: Yup.string().when("censusType", {
    is: "Owner",
    then: Yup.string()
      .matches(numberRegex, "Numbers only, please.")
      .required("You missed this one")
      .test(
        "valid percentage",
        "Please enter a value between 0 - 100",
        (value) => {
          const val = parseInt(value || "0", 10);
          return 0 <= val && val <= 100;
        }
      ),
  }),
  workersCompensationClassification: Yup.string().when("censusType", {
    is: "Employee",
    then: Yup.string()
      .optional()
      .test(
        "no emoji",
        "Sorry, no emoji 😔",
        (value) => !noEmojiRegex.test(value || "")
      ),
  }),
  exemptionStatus: Yup.string()
    .optional()
    .when(["censusType", "isPaid"], {
      is: (censusType: string, isPaid: string) =>
        (censusType === "Owner" && isPaid === "Paid") ||
        censusType === "Employee",
      then: Yup.string().required("You missed this one"),
    }),
  isPaid: Yup.string().when("censusType", {
    is: "Owner",
    then: Yup.string()
      .required("You missed this one")
      .test(
        "no emoji",
        "Sorry, no emoji 😔",
        (value) => !noEmojiRegex.test(value || "")
      ),
  }),
  employeeType: Yup.string().when("censusType", {
    is: "Employee",
    then: Yup.string()
      .required("You missed this one")
      .test(
        "no emoji",
        "Sorry, no emoji 😔",
        (value) => !noEmojiRegex.test(value || "")
      ),
  }),
  salaryType: Yup.string()
    .optional()
    .when(["censusType", "isPaid"], {
      is: (censusType: string, isPaid: string) =>
        (censusType === "Owner" && isPaid === "Paid") ||
        censusType === "Employee",
      then: Yup.string().required("You missed this one"),
    }),
  hourlyRate: Yup.string().when("salaryType", {
    is: "Hourly rate",
    then: Yup.string()
      .required("You missed this one")
      .test(
        "no emoji",
        "Sorry, no emoji 😔",
        (value) => !noEmojiRegex.test(value || "")
      )
      .test(
        "greater than 0",
        "Must be greater than 0.",
        (value) => parseFloat(value || "") > 0
      )
      .test("invalid wage format", "Please enter a valid wage", wageCheck),
  }),
  salary: Yup.string().when("salaryType", {
    is: "Annual salary",
    then: Yup.string()
      .required("You missed this one")
      .test(
        "no emoji",
        "Sorry, no emoji 😔",
        (value) => !noEmojiRegex.test(value || "")
      )
      .test(
        "greater than 0",
        "Must be greater than 0.",
        (value) => parseFloat(value || "") > 0
      )
      .test("invalid wage format", "Please enter a valid wage", wageCheck),
  }),
  hoursWeekly: Yup.string()
    .matches(numberRegex, "Numbers only, please.")
    .when("salaryType", {
      is: "Hourly rate",
      then: Yup.string()
        .required("You missed this one")
        .test(
          "no emoji",
          "Sorry, no emoji 😔",
          (value) => !noEmojiRegex.test(value || "")
        )
        .test(
          "min hours",
          "The maximum number of hours is 168.",
          (value) => parseInt(value || "") <= 168
        )
        .test(
          "min hours",
          "The minimum number of hours is 1.",
          (value) => parseInt(value || "") >= 1
        ),
    }),
  adminAccess: Yup.string().when("censusType", {
    is: "Employee",
    then: Yup.string()
      .required("You missed this one")
      .test(
        "no emoji",
        "Sorry, no emoji 😔",
        (value) => !noEmojiRegex.test(value || "")
      ),
  }),
});
